import React from "react";
import { Field, ErrorMessage } from "formik";
import { Plan } from "../dataTypes";
import { Users, User, Mail, DollarSign, Calendar, Infinity } from "lucide-react";

interface PricingLimitsTabProps {
  values: Plan;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
}

const PricingLimitsTab: React.FC<PricingLimitsTabProps> = ({
  values,
  setFieldValue,
  onPrevious,
  onNext,
}) => {
  const toggleLimit = (field: 'userLimit' | 'contactLimit') => {
    const currentValue = values[field];
    if (currentValue === 0) {
      setFieldValue(field, field === 'userLimit' ? 5 : 1000, true);
    } else {
      setFieldValue(field, 0, true);
    }
  };

  const getLimitDisplay = (field: 'userLimit' | 'contactLimit') => {
    const value = values[field];
    return value === 0 ? "Unlimited" : value.toLocaleString();
  };

  const calculateYearlyDiscount = () => {
    const monthlyPrice = values.prices[0]?.amount || 0;
    const yearlyPrice = values.prices[1]?.amount || 0;
    
    if (monthlyPrice > 0 && yearlyPrice > 0) {
      const monthlyTotal = monthlyPrice * 12;
      const discount = ((monthlyTotal - yearlyPrice) / monthlyTotal) * 100;
      return Math.round(discount);
    }
    return 0;
  };

  const discount = calculateYearlyDiscount();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
          Pricing & Limits
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configure your pricing options and set usage limits
        </p>
      </div>

      {/* Pricing Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Pricing</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Set up billing options</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Price */}
          <div className={`border rounded-lg p-6 transition-colors ${
            values.prices[0]?.active 
              ? 'border-blue-300 bg-blue-50/30 dark:bg-blue-900/10 dark:border-blue-700' 
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Monthly</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Billed monthly</p>
                </div>
              </div>
              
              <label className="flex items-center cursor-pointer">
                <Field
                  type="checkbox"
                  name="prices.0.active"
                  className="sr-only"
                />
                <div className={`relative w-12 h-6 rounded-full transition-colors ${
                  values.prices[0]?.active 
                    ? 'bg-blue-600' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}>
                  <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    values.prices[0]?.active ? 'translate-x-6' : ''
                  }`} />
                </div>
              </label>
            </div>
            
            {values.prices[0]?.active && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Monthly Price
                  </label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Field
                        name="prices.0.amount"
                        type="number"
                        step="0.01"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="w-20">
                      <Field
                        name="prices.0.currency"
                        type="text"
                        className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-center"
                        placeholder="USD"
                      />
                    </div>
                  </div>
                  <ErrorMessage name="prices.0.amount" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            )}
          </div>

          {/* Yearly Price */}
          <div className={`border rounded-lg p-6 transition-colors ${
            values.prices[1]?.active 
              ? 'border-green-300 bg-green-50/30 dark:bg-green-900/10 dark:border-green-700' 
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Yearly</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Billed annually</p>
                </div>
              </div>
              
              <label className="flex items-center cursor-pointer">
                <Field
                  type="checkbox"
                  name="prices.1.active"
                  className="sr-only"
                />
                <div className={`relative w-12 h-6 rounded-full transition-colors ${
                  values.prices[1]?.active 
                    ? 'bg-green-600' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}>
                  <div className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    values.prices[1]?.active ? 'translate-x-6' : ''
                  }`} />
                </div>
              </label>
            </div>
            
            {values.prices[1]?.active && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yearly Price
                  </label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Field
                        name="prices.1.amount"
                        type="number"
                        step="0.01"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="w-20">
                      <Field
                        name="prices.1.currency"
                        type="text"
                        className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-center"
                        placeholder="USD"
                      />
                    </div>
                  </div>
                  <ErrorMessage name="prices.1.amount" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                {discount > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-sm text-green-700 dark:text-green-300 text-center">
                      Save {discount}% compared to monthly
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Limits Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Usage Limits</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Set capacity limits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Limit */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">User Limit</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Team members</p>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => toggleLimit('userLimit')}
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                  values.userLimit !== 0 
                    ? 'bg-blue-600' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  values.userLimit !== 0 ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {values.userLimit !== 0 ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Users
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Field
                      name="userLimit"
                      type="number"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                  <ErrorMessage name="userLimit" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Infinity className="h-6 w-6 text-gray-400 mr-2" />
                <span className="text-gray-600 dark:text-gray-400">Unlimited Users</span>
              </div>
            )}
            
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Current: <span className="font-medium text-blue-600">{getLimitDisplay('userLimit')}</span> users
              </p>
            </div>
          </div>

          {/* Contact Limit */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Contact Limit</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customer database</p>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => toggleLimit('contactLimit')}
                className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                  values.contactLimit !== 0 
                    ? 'bg-green-600' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  values.contactLimit !== 0 ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            {values.contactLimit !== 0 ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Contacts
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Field
                      name="contactLimit"
                      type="number"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                  <ErrorMessage name="contactLimit" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Infinity className="h-6 w-6 text-gray-400 mr-2" />
                <span className="text-gray-600 dark:text-gray-400">Unlimited Contacts</span>
              </div>
            )}
            
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Current: <span className="font-medium text-green-600">{getLimitDisplay('contactLimit')}</span> contacts
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Back
        </button>
        
        <button
          type="button"
          onClick={onNext}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          Continue to Features
        </button>
      </div>
    </div>
  );
};

export default PricingLimitsTab;